<!-- cdkFocusInitial + tabindex="-1": round 3 fix — this used to sit on the
     trailing Close button in the actions row. NvDialogService sets no
     autoFocus, so CDK's default 'first-tabbable' path calls
     FocusTrap.focusInitialElement(), which focuses whatever carries
     cdk-focus-initial with no preventScroll — the browser then scrolls the
     nearest scrollable ancestor (.nv-dialog-panel) to reveal it. On the
     Close button that meant scrolling straight to the bottom on open,
     clipping every order above it (the panel itself has no visible
     scrollbar to recover with). Anchoring it to the title instead means
     that same auto-scroll-into-view lands the dialog exactly where it
     should already be: the top. h2 isn't natively focusable, hence
     tabindex="-1" (focusable programmatically, not part of tab order). -->
<h2 nvDialogTitle class="nv-td__title" cdkFocusInitial tabindex="-1">
  @if (singleOrderMode() && tableOrders()[0]; as onlyOrder) {
    <span>Order #{{ onlyOrder.id.slice(0, 6).toUpperCase() }} · Table {{ table()?.number }}</span>
  } @else {
    <span>Table {{ table()?.number }}</span>
    @if (statusView(); as view) {
      <nv-status [tone]="view.tone" size="chip" [label]="view.label" />
    }
  }
</h2>
<div nvDialogContent>
  @if (table(); as t) {
    <!-- Round 8, §10 — Name/Number on the left (unchanged position), Persons
         moved into the same row on the right, instead of a third stacked
         block below. -->
    <div class="nv-td__header-row">
      <div class="nv-td__identity">
        <p class="nv-td__guest">
          @if (t) {
            {{ (status()?.occupied) ? (status()?.guestName || 'Guest seated') : 'No guest seated right now' }}
          }
        </p>
        <!-- Round 4, §3f — the floor card already shows this (floor-view.html's
             own canSeePhone() line) but the dialog never printed it at all,
             despite TableStatus.guestPhone already being loaded and in scope.
             Same permission gate as the card, so the two surfaces never
             disagree about who may see a guest's number. -->
        @if (status()?.occupied && canSeePhone() && status()?.guestPhone) {
          <p class="nv-td__phone">
            <nv-icon [size]="15">call</nv-icon>
            {{ status()?.guestPhone }}
          </p>
        }
      </div>
      <!-- Round 4, §3e — table-scoped, so hidden in singleOrderMode; editable
           (not just displayed) because a party's size genuinely changes
           mid-visit — see the class's own doc comment. -->
      @if (status()?.occupied && !singleOrderMode()) {
        <div class="nv-td__party">
          <nv-icon [size]="15">groups</nv-icon>
          <input
            class="nv-td__party-input"
            type="number"
            min="1"
            max="60"
            placeholder="Persons"
            aria-label="Number of persons"
            [ngModel]="partySizeInput()"
            (ngModelChange)="onPartySizeInput($any($event))"
          />
          @if (partySizeChanged()) {
            <button type="button" nvButton="text" size="xs" [disabled]="savingPartySize()" (click)="savePartySize()">
              @if (savingPartySize()) {
                <nv-spinner [diameter]="14" />
              } @else {
                Save
              }
            </button>
          }
        </div>
      }
    </div>
  }

  <section class="nv-td-section">
    <div class="nv-td-section__head">
      <h3 class="nv-td-section__title">{{ singleOrderMode() ? 'Order' : 'Orders so far' }}</h3>
      <span class="nv-td-total">
        {{ singleOrderMode() ? 'Order total' : 'Visit total' }} ₹{{ formatPrice(visitTotalMinor()) }}
      </span>
    </div>

    @if (orderViews().length === 0) {
      <p class="nv-td-empty">{{ singleOrderMode() ? 'This order is no longer open.' : 'No orders yet this visit.' }}</p>
    } @else {
      @for (ov of orderViews(); track ov.order.id) {
        <div class="nv-td-order">
          <div class="nv-td-order__head">
            <span class="nv-td-order__id">Order #{{ ov.order.id.slice(0, 6).toUpperCase() }}</span>
            <span class="nv-td-order__total">₹{{ formatPrice(ov.order.totalMinor) }}</span>
          </div>

          <ul class="nv-td-order__lines">
            @for (lv of ov.lines; track lv.line.lineId) {
              <li
                class="nv-td-line"
                [class.nv-td-line--served]="lv.state === 'served'"
                [class.nv-td-line--unavailable]="lv.state === 'unavailable'"
              >
                <span class="nv-td-line__name">
                  {{ lv.line.quantity }}× {{ lv.line.name }}
                  @if (kotLabel(lv.line.kotNo); as kot) {
                    <span class="nv-td-line__kot">{{ kot }}</span>
                  }
                </span>
                @if (lv.state !== 'unavailable' && lineChip(lv.line); as chip) {
                  <span [class]="'nv-td-line__chip nv-td-line__chip--' + chip.kind" [attr.data-testid]="chip.testId">{{ chip.label }}</span>
                }
                @if (lv.state === 'ready' && canManage()) {
                  <button
                    type="button"
                    class="nv-td-line__btn"
                    [disabled]="isServingLine(ov.order.id, lv.line.lineId)"
                    (click)="markLineServed(ov.order.id, lv.line.lineId)"
                  >
                    @if (isServingLine(ov.order.id, lv.line.lineId)) {
                      <nv-spinner [diameter]="14" />
                    } @else {
                      Served
                    }
                  </button>
                } @else if (lv.state === 'in_kitchen' && canManageKot() && ticketForLine(ov.order.id, lv.line.lineId); as ticket) {
                  <!-- bugfix/core-workflow-issues — one-tap shortcut past the
                       separate Kitchen tickets board; see
                       fastTrackTicketReady's own doc comment. -->
                  <button
                    type="button"
                    class="nv-td-line__btn"
                    [disabled]="isFastTrackingTicket(ticket.id)"
                    (click)="fastTrackTicketReady(ticket)"
                  >
                    @if (isFastTrackingTicket(ticket.id)) {
                      <nv-spinner [diameter]="14" />
                    } @else {
                      Mark ready
                    }
                  </button>
                } @else if (!lineChip(lv.line) || lv.state === 'unavailable') {
                  <span class="nv-td-line__state">{{ lineStateLabel[lv.state] }}</span>
                }
              </li>
            }
          </ul>

          <div class="nv-td-order__actions">
            @if (canManage() && ov.readyLineIds.length > 0) {
              <button
                type="button"
                nvButton="text"
                size="xs"
                [disabled]="isServingOrder(ov.order.id)"
                (click)="markAllServed(ov.order.id)"
              >
                @if (isServingOrder(ov.order.id)) {
                  <nv-spinner [diameter]="14" />
                } @else {
                  Mark all served
                }
              </button>
            }
            @if (canManage() && ov.order.status === 'pending_approval') {
              <button type="button" nvButton="primary" size="xs" (click)="showItems(ov.order)">Show items</button>
              <button type="button" nvButton="text" size="xs" (click)="reject(ov.order)">Reject</button>
            }
          </div>
        </div>
      }
    }
  </section>

  <!-- Round 4, §4 — a per-order view has no "rest of the table" to report requests for; this section is table-scoped and stays hidden in singleOrderMode. -->
  @if (!singleOrderMode()) {
    <section class="nv-td-section">
      <h3 class="nv-td-section__title">Pending requests</h3>
      @if (pendingRequests().length === 0) {
        <p class="nv-td-empty">Nothing waiting.</p>
      } @else {
        <ul class="nv-td-requests">
          @for (r of pendingRequests(); track r.id) {
            <li class="nv-td-request">
              <div class="nv-td-request__text">
                <span class="nv-td-request__reason">{{ reasonLabel[r.reason] }}</span>
                @if (r.note) {
                  <span class="nv-td-request__note">“{{ r.note }}”</span>
                }
              </div>
              @if (r.reason === 'pin_reset' && canSetPin()) {
                <button type="button" nvButton="primary" size="xs" (click)="openSetPin(r)">Set PIN</button>
              }
              <button
                type="button"
                nvButton="primary"
                size="xs"
                [disabled]="isAcknowledging(r.id)"
                (click)="acknowledgeRequest(r.id)"
              >
                {{ isAcknowledging(r.id) ? 'Acknowledging…' : 'Acknowledge' }}
              </button>
            </li>
          }
        </ul>
      }
    </section>
  }
</div>
<div nvDialogActions>
  @if (!singleOrderMode() && status()?.occupied) {
    <!-- Item 16 — disabled (not error-after-tap) while this table's bill is
         still unsettled, same condition floor-view's own card gates on. -->
    <button
      type="button"
      nvButton="secondary"
      tone="danger"
      [disabled]="clearing() || hasUnsettledBill()"
      [attr.title]="clearBlockedReason()"
      (click)="clearTable()"
    >
      @if (clearing()) {
        <nv-spinner [diameter]="14" />
      } @else {
        Clear table
      }
    </button>
    @if (clearBlockedReason(); as reason) {
      <span class="nv-table-detail__clear-hint">{{ reason }}</span>
    }
  }
  <button type="button" nvButton="secondary" (click)="close()">Close</button>
</div>
